import { Alert, AlertTitle, Box, Button, Typography } from "@mui/material";
import { useCallback, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import type { ApiError } from "@entrilia/shared/api/types";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import HorizontalFill from "@entrilia/shared/components/HorizontalFill";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import useFetch from "@entrilia/shared/hooks/useFetch";
import usePageTitle from "@entrilia/shared/hooks/usePageTitle";
import { logError } from "@entrilia/shared/logging";
import { defined } from "@entrilia/shared/utilities/typeHelper";
import adminApi from "../../../api/adminApi";
import type { ResourceChangeProgressNotification } from "../../../api/types/resourceChangesTypes";
import useClientContext from "../../../context/useClientContext";
import useResourceChanged from "../../../hooks/useResourceChanged";
import { pageRoutes } from "../../../routes";
import GeneralPageFooter from "../../common/GeneralPageFooter";
import GeneralPageHeader from "../../common/GeneralPageHeader";
import DeleteDataImportDialog from "./DeleteDataImportDialog";
import FullPageStatusProgress from "./FullPageStatusProgress";
import RunningOperationAlert from "./RunningOperationAlert";
import StartImportConfirmationDialog from "./StartImportConfirmationDialog";
import ReviewImport from "./csv-import/review-import/ReviewImport";
import ReviewImportActions from "./csv-import/review-import/ReviewImportActions";
import type { DataImportResult } from "./dataImportOperations";
import { useApplyDataImport, useValidateDataImport } from "./dataImportOperations";
import ImportDataDetailsTitle from "./details/ImportDataDetailsTitle";
import type { OperationStatus, StartDataImportOptions } from "./importDataPagesTypes";
import {
  getPermissionsToManageImport,
  isImportApplicationAllowed,
  isImportDeletable,
  isImportEditable,
  isImportOperationRunning,
} from "./importDataStateHelper";

interface DialogState {
  openDialog?: "start_import" | "delete_import";
  errorsCount?: number;
  deleteRecordsCount?: number;
}

interface OperationError {
  title: string;
  details: string;
}

const getDataImportChangesTotals = withErrorHandling(adminApi.getDataImportChangesTotals);

const ImportDataReviewPage = () => {
  usePageTitle("Data Import");

  const { id } = useParams();
  const { clientCode, hasPermissions } = useClientContext();
  const { sendNotification } = useNotificationContext();
  const navigate = useNavigate();

  const [validateImportStatus, setValidateImportStatus] = useState<OperationStatus>();
  const [applyImportStatus, setApplyImportStatus] = useState<OperationStatus>();
  const [liveProgress, setLiveProgress] = useState<ResourceChangeProgressNotification>();
  const [operationError, setOperationError] = useState<OperationError>();
  const [dialogState, setDialogState] = useState<DialogState>({});

  const getImportDetails = useCallback(() => adminApi.getDataImportDetails(defined(id)), [id]);
  const getMetadata = useCallback(() => adminApi.getDataImportMetadata(defined(id)), [id]);

  const [
    dataImport,
    fetchImportError,
    {
      isFetching: isFetchingImportDetails,
      setData: setDataImport,
      fetch: fetchDataImport,
      fetchSilent: fetchDataImportSilent,
    },
  ] = useFetch(getImportDetails);

  const [
    dataImportMetadata,
    fetchMetadataError,
    { isFetching: isFetchingImportMetadata, setData: setDataImportMetadata },
  ] = useFetch(getMetadata);

  const handleValidateStatusUpdate = useCallback((status: OperationStatus) => setValidateImportStatus(status), []);

  const handleValidateComplete = useCallback(
    (result: DataImportResult) => {
      setValidateImportStatus(undefined);
      setOperationError(undefined);
      setDataImport(result.dataImport);
      setDataImportMetadata(result.dataImportMetadata);
    },
    [setDataImport, setDataImportMetadata]
  );

  const handleValidateError = useCallback((error: ApiError) => {
    setValidateImportStatus(undefined);
    logError(error, "[ImportDataReviewPage] validateDataImportWithTimeout");
    setOperationError({ title: "Validation Error", details: error.message });
  }, []);

  const startValidateDataImport = useValidateDataImport({
    onStatusUpdate: handleValidateStatusUpdate,
    onComplete: handleValidateComplete,
    onError: handleValidateError,
  });

  const handleApplyStatusUpdate = useCallback((status: OperationStatus) => setApplyImportStatus(status), []);

  const handleApplyComplete = useCallback(() => {
    setApplyImportStatus(undefined);
    setOperationError(undefined);
    sendNotification("Data import has been applied successfully");
    navigate(`/${clientCode}/${pageRoutes.settings}/${pageRoutes.importData}`);
  }, [clientCode, navigate, sendNotification]);

  const handleApplyError = useCallback((error: ApiError) => {
    setApplyImportStatus(undefined);
    logError(error, "[ImportDataReviewPage] applyDataImportWithTimeout");
    setOperationError({ title: "Apply Import Error", details: error.message });
  }, []);

  const startApplyDataImport = useApplyDataImport({
    onStatusUpdate: handleApplyStatusUpdate,
    onComplete: handleApplyComplete,
    onError: handleApplyError,
  });

  const handleDataImportChanged = async () => {
    const error = await fetchDataImportSilent();
    if (error) {
      logError(error, "[ImportDataReviewPage] fetchDataImportSilent");
    }

    setLiveProgress(undefined);
  };

  useResourceChanged("DataImport", defined(id), {
    onChanged: handleDataImportChanged,
    onProgress: setLiveProgress,
    isOperationRunning: dataImport !== undefined && isImportOperationRunning(dataImport),
  });

  if (fetchImportError || fetchMetadataError) {
    logError(fetchImportError || fetchMetadataError, "[ImportCsvDataReviewPage]");
    return <DataLoadingFailed title="Loading data import details failed" />;
  }

  if (
    dataImport === undefined ||
    dataImportMetadata === undefined ||
    isFetchingImportDetails ||
    isFetchingImportMetadata
  ) {
    return <InlineLoader />;
  }

  if (validateImportStatus !== undefined) {
    return (
      <FullPageStatusProgress
        title="We are validating the changes. This may take some time."
        operationStatus={validateImportStatus}
      />
    );
  }

  if (applyImportStatus !== undefined) {
    return (
      <FullPageStatusProgress
        title="We are applying the changes. This may take some time."
        operationStatus={applyImportStatus}
      />
    );
  }

  const validateImport = () => {
    startValidateDataImport(defined(id));
  };

  const applyImport = (options: StartDataImportOptions) => {
    startApplyDataImport(defined(id), options);
  };

  const handleStartImportButtonClick = async () => {
    const [resp, totalsFetchError] = await getDataImportChangesTotals(dataImport.id);
    if (totalsFetchError) {
      logError(totalsFetchError, "[ImportDataReviewPage] getDataImportChangesTotals");
      setOperationError({ title: "Apply Import Error", details: "Could not determine records' statuses" });
      return;
    }

    setDialogState({
      openDialog: "start_import",
      errorsCount: resp.validationErrorsCount,
      deleteRecordsCount: resp.deleteRecordsCount,
    });
  };

  const handleConfirmImport = (options: StartDataImportOptions) => {
    setDialogState({});
    applyImport(options);
  };

  const handleDeleteImport = () => {
    setDialogState({ openDialog: "delete_import" });
  };

  const handleImportDeleted = () => {
    setDialogState({});
    navigate(`/${clientCode}/${pageRoutes.settings}/${pageRoutes.importData}`);
  };

  const backButtonPath = isImportEditable(dataImport.state)
    ? `/${clientCode}/${pageRoutes.settings}/${pageRoutes.importData}`
    : `/${clientCode}/${pageRoutes.settings}/${pageRoutes.importData}/${dataImport.id}/${pageRoutes.importDetails}`;

  const hasPermissionsToManage = hasPermissions(getPermissionsToManageImport(dataImport.entriliaProductAreas));
  const isOperationRunning = isImportOperationRunning(dataImport);

  return (
    <>
      <GeneralPageHeader
        TitleComponent={<ImportDataDetailsTitle dataImportDetails={dataImport} />}
        showDefaultBackButtonTo={backButtonPath}
      >
        <ReviewImportActions
          dataImport={dataImport}
          showDownload
          showValidate={hasPermissionsToManage && isImportEditable(dataImport.state) && !isOperationRunning}
          showDelete={hasPermissionsToManage && isImportDeletable(dataImport.state) && !isOperationRunning}
          onValidate={validateImport}
          onDelete={handleDeleteImport}
        />
      </GeneralPageHeader>
      {operationError && (
        <Box sx={{ pt: 2, pb: 1, px: 2.5, width: "30rem" }}>
          <Alert severity="error" onClose={() => setOperationError(undefined)}>
            <AlertTitle>{operationError?.title}</AlertTitle>
            <Typography>{operationError?.details}</Typography>
          </Alert>
        </Box>
      )}
      {isOperationRunning && (
        <Box sx={{ pt: 2, pb: 1, px: 2.5, width: "30rem" }}>
          <RunningOperationAlert dataImport={dataImport} progress={liveProgress} onRefresh={fetchDataImport} />
        </Box>
      )}
      <ReviewImport dataImport={dataImport} dataImportMetadata={dataImportMetadata} />
      {hasPermissionsToManage && (
        <GeneralPageFooter>
          <HorizontalFill />
          <Button
            variant="contained"
            onClick={handleStartImportButtonClick}
            disabled={applyImportStatus || !isImportApplicationAllowed(dataImport.state) || isOperationRunning}
          >
            Start Import
          </Button>
        </GeneralPageFooter>
      )}
      {dialogState.openDialog === "start_import" && (
        <StartImportConfirmationDialog
          dataImport={dataImport}
          onClose={() => setDialogState({})}
          onConfirm={handleConfirmImport}
          errorsCount={dialogState.errorsCount ?? 0}
          deleteRecordsCount={dialogState.deleteRecordsCount ?? 0}
        />
      )}
      {dialogState.openDialog === "delete_import" && (
        <DeleteDataImportDialog
          dataImport={dataImport}
          onClose={() => setDialogState({})}
          onConfirm={handleImportDeleted}
        />
      )}
    </>
  );
};

export default ImportDataReviewPage;
